import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { getQuickRepliesList, cacheQuickReplies, type QuickReplyItem } from '../utils/quick-replies';
import QuickRepliesManagerModal from './QuickRepliesManagerModal';

interface QuickRepliesModalProps {
  show: boolean;
  searchQuery: string;
  onSelect: (response: string) => void;
  onClose: () => void;
  onForceClose?: () => void;
  inputRef: React.RefObject<HTMLTextAreaElement>;
}

const QuickRepliesModal: React.FC<QuickRepliesModalProps> = ({
  show,
  searchQuery,
  onSelect,
  onClose,
  onForceClose,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showManager, setShowManager] = useState(false);
  const [quickReplies, setQuickReplies] = useState<QuickReplyItem[]>([]);
  const [loading, setLoading] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Cargar respuestas rápidas desde API - solo una vez
  useEffect(() => {
    const loadQuickReplies = async () => {
      try {
        setLoading(true);
        const replies = await getQuickRepliesList();
        setQuickReplies(replies);
        cacheQuickReplies(replies);
      } catch (error) {
        console.error('Error loading quick replies:', error);
      } finally {
        setLoading(false);
      }
    };

    loadQuickReplies();
  }, []);

  const filteredReplies = useMemo(() => {
    if (!show) return [];

    if (searchQuery === '') {
      return quickReplies;
    }

    return quickReplies.filter(reply => 
      reply.trigger.toLowerCase().startsWith(searchQuery.toLowerCase())
    );
  }, [searchQuery, quickReplies, show]);

  const isVisible = show && !showManager;

  const scrollToSelected = useCallback(() => {
    if (scrollContainerRef.current && filteredReplies.length > 0) {
      const selectedItem = scrollContainerRef.current.children[selectedIndex] as HTMLElement;
      selectedItem?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedIndex, filteredReplies.length]);

  useEffect(() => {
    if (filteredReplies.length > 0) {
      setSelectedIndex(0);
    }
  }, [filteredReplies.length]);

  useEffect(() => {
    scrollToSelected();
  }, [selectedIndex, scrollToSelected]);

  useEffect(() => {
    if (!show) {
      onClose();
      onForceClose?.();
    }
  }, [show, onClose, onForceClose]);

  useEffect(() => {
    if (!show || showManager || !isVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isVisible || filteredReplies.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => (prev + 1) % filteredReplies.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => prev === 0 ? filteredReplies.length - 1 : prev - 1);
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredReplies[selectedIndex]) {
            onSelect(filteredReplies[selectedIndex].response);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [show, filteredReplies, selectedIndex, onSelect, onClose, showManager, isVisible]);

  const handleUpdateReplies = useCallback((updatedReplies: QuickReplyItem[]) => {
    setQuickReplies(updatedReplies);
    cacheQuickReplies(updatedReplies);
  }, []);

  const handleOpenManager = useCallback(() => {
    setShowManager(true);
  }, []);

  if (!show) {
    return null;
  }

  return (
    <React.Fragment>
      {show && !showManager && (
        <div
            ref={modalRef}
            className="position-absolute bg-white border-0 rounded-3 shadow d-flex flex-column"
            style={{
              bottom: '100%',
              left: 0,
              right: 0,
              marginBottom: '8px',
              maxHeight: '260px',
              zIndex: 1000,
              display: isVisible ? 'flex' : 'none',
              opacity: isVisible ? 1 : 0,
              visibility: isVisible ? 'visible' : 'hidden',
              transform: isVisible ? 'translateY(0)' : 'translateY(-10px)',
              transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
              minHeight: isVisible ? '120px' : '0',
              willChange: 'transform, opacity'
            }}
        >
          {/* HEADER */}
          <div className="px-3 py-2 d-flex justify-content-between align-items-center border-bottom rounded-top"
               style={
                 {backgroundColor: '#d19221'}
               }>
            <div className="fw-semibold text-white small">Plantillas</div>

            <button
                type="button"
                className="btn p-1 d-flex align-items-center justify-content-center"
                style={{ background: 'transparent', border: 'none' }}
                title="Editar respuestas"
                onClick={handleOpenManager}
            >
              <i className="ri-pencil-line text-white"></i>
            </button>
          </div>

          <div 
            ref={scrollContainerRef}
            className="py-3 px-3 d-flex flex-column gap-3 custom-scrollbar"
            style={{
              overflowY: 'auto',
              maxHeight: 'calc(260px - 48px)',
              minHeight: '72px',
              scrollbarWidth: 'thin',
              scrollbarColor: '#d1d5db #f9fafb'
            }}
          >
            {loading ? (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm text-primary" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
                <div className="small text-muted mt-2">Cargando respuestas rápidas...</div>
              </div>
            ) : filteredReplies.length > 0 ? (
              filteredReplies.map((reply, index) => {
                const isActive = index === selectedIndex;

                return (
                    <div
                        key={reply.id}
                        onClick={() => onSelect(reply.response)}
                        onMouseEnter={() => setSelectedIndex(index)}
                        className="px-3 py-2 d-flex align-items-start"
                        style={{
                          cursor: 'pointer',
                          background: isActive ? '#f1f3f5' : 'transparent',
                          transition: 'background 0.2s ease',
                          borderRadius: '8px'
                        }}
                    >
                      <div className="flex-grow-1">
                        <div className={`fw-medium ${isActive ? 'text-dark' : 'text-body'}`}>
                          /{reply.trigger}
                        </div>

                        <div
                            className="small"
                            style={{
                              color: isActive ? '#6c757d' : '#9aa0a6'
                            }}
                        >
                          {reply.response}
                        </div>
                      </div>
                    </div>
                );
              })
            ) : (
              <div className="text-center py-3">
                <div className="small text-muted">
                  No se encontraron respuestas rápidas
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showManager && (
        <QuickRepliesManagerModal
          show={showManager}
          replies={quickReplies}
          onClose={() => setShowManager(false)}
          onUpdate={handleUpdateReplies}
        />
      )}
    </React.Fragment>
  );
};

export default QuickRepliesModal;
